import React, { useState, useEffect } from "react";

const NAV_TABS = [
  { id: "home", icon: "🏠", label: "Home" },
  { id: "school", icon: "🏫", label: "School & Activities" },
  { id: "links", icon: "🔗", label: "Quick Links" },
  { id: "essential", icon: "🔒", label: "Essential Info" },
];

async function updateSchool(pageId, value) {
  await fetch("/.netlify/functions/notion", {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ pageId, action: "updateSchool", value }),
  });
}

function RichText({ text, urls }) {
  if (!urls || urls.length === 0) {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);
    if (parts.length === 1) return <span style={textValue}>{text}</span>;
    return (
      <span style={textValue}>
        {parts.map((part, i) =>
          urlRegex.test(part)
            ? <a key={i} href={part} target="_blank" rel="noreferrer" style={{ color: "var(--sage)", textDecoration: "underline" }}>{part}</a>
            : <span key={i}>{part}</span>
        )}
      </span>
    );
  }
  return (
    <span style={textValue}>
      {urls.map((u, i) => (
        <a key={i} href={u.url} target="_blank" rel="noreferrer" style={{ color: "var(--sage)", textDecoration: "underline" }}>{u.text}</a>
      ))}
    </span>
  );
}

export default function SchoolDetail({ school, onBack, onTabTap, readOnly = false }) {
  const [showEdit, setShowEdit] = useState(false);
  const [form, setForm] = useState({
    specialDays: school.specialDays || "",
    notes: school.notes || "",
    teachers: school.teachers || "",
    breakfastMenu: school.breakfastMenu || "",
    lunchMenu: school.lunchMenu || "",
  });
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(school);
  const [blocks, setBlocks] = useState([]);

  useEffect(() => {
    fetch(`/.netlify/functions/notion?type=blocks&pageId=${school.id}`)
      .then(r => r.json()).then(d => setBlocks(d.blocks || [])).catch(() => {});
  }, [school.id]);

  const handleSave = async () => {
    setSaving(true);
    await updateSchool(school.id, form);
    setData({ ...data, ...form });
    setSaving(false);
    setShowEdit(false);
  };

  const yearRange = school.schoolYearStart && school.schoolYearEnd
    ? `${new Date(school.schoolYearStart + "T00:00:00").getFullYear()}–${new Date(school.schoolYearEnd + "T00:00:00").getFullYear()}`
    : null;

  return (
    <>
      <div className="app">
        <header style={{ background: "var(--white)", padding: "54px 20px 16px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 12 }}>
          <button onClick={onBack} style={{ width: 34, height: 34, borderRadius: "50%", background: "var(--cream-hover)", border: "none", fontSize: 18, color: "var(--mid)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "inherit" }}>‹</button>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: "var(--amber-muted)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>🏫</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, fontWeight: 600, color: "var(--charcoal)", lineHeight: 1.2 }}>{data.name}</div>
            {yearRange && <div style={{ fontSize: 13, color: "var(--muted)", fontWeight: 500, marginTop: 3 }}>{yearRange}</div>}
          </div>
          {!readOnly && <button onClick={() => setShowEdit(true)} style={{ background: "none", border: "none", fontSize: 13, fontWeight: 800, color: "var(--sage)", cursor: "pointer", fontFamily: "inherit" }}>Edit</button>}
        </header>

        <div className="scroll-body" style={{ padding: "12px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={card}>
            <div style={cardTitle}>Contact</div>
            <div style={grid2}>
              <div style={cell}>
                <div style={cellLabel}>Phone</div>
                {data.mainPhone
                  ? <a href={`tel:${data.mainPhone.replace(/\D/g,'')}`} style={{ ...cellValue, color: "var(--sage)", textDecoration: "none" }}>{data.mainPhone}</a>
                  : <div style={{ ...cellValue, color: "var(--muted)" }}>TBD</div>}
              </div>
              <div style={{ ...cell, borderRight: "none", borderBottom: "none" }}>
                <div style={cellLabel}>Email</div>
                {data.officeEmail
                  ? <a href={`mailto:${data.officeEmail}`} style={{ ...cellValue, color: "var(--sage)", textDecoration: "none", fontSize: 13 }}>{data.officeEmail}</a>
                  : <div style={{ ...cellValue, color: "var(--muted)" }}>TBD</div>}
              </div>
            </div>
          </div>

          <div style={card}>
            <div style={cardTitle}>Schedule</div>
            <div style={grid2}>
              <div style={cell}>
                <div style={cellLabel}>Start</div>
                <div style={cellValue}>{data.startTime || "TBD"}</div>
              </div>
              <div style={{ ...cell, borderRight: "none" }}>
                <div style={cellLabel}>Dismissal</div>
                <div style={cellValue}>{data.dismissalTime || "TBD"}</div>
              </div>
              {(data.busPickupTime || data.busDropoffTime) && (
                <>
                  <div style={{ ...cell, borderBottom: "none" }}>
                    <div style={cellLabel}>Bus Pickup</div>
                    <div style={cellValue}>{data.busPickupTime || "—"}</div>
                  </div>
                  <div style={{ ...cell, borderRight: "none", borderBottom: "none" }}>
                    <div style={cellLabel}>Bus Dropoff</div>
                    <div style={cellValue}>{data.busDropoffTime || "—"}</div>
                  </div>
                </>
              )}
            </div>
          </div>

          <div style={card}>
            <div style={cardTitle}>Staff</div>
            <div style={textRow}>
              <div style={textLabel}>Principal</div>
              <div style={textValue}>{data.principal || "TBD"}</div>
            </div>
            <div style={{ ...textRow, borderBottom: "none" }}>
              <div style={textLabel}>Teachers</div>
              <div style={textValue}>{data.teachers || "TBD"}</div>
            </div>
          </div>

          {(data.specialDays || data.notes || data.dressCode || data.address) && (
            <div style={card}>
              <div style={cardTitle}>Details</div>
              {data.specialDays && <div style={textRow}><div style={textLabel}>Special Days</div><div style={textValue}>{data.specialDays}</div></div>}
              {data.notes && <div style={textRow}><div style={textLabel}>Notes</div><div style={textValue}>{data.notes}</div></div>}
              {data.dressCode && <div style={textRow}><div style={textLabel}>Dress Code</div><div style={textValue}>{data.dressCode}</div></div>}
              {data.address && <div style={{ ...textRow, borderBottom: "none" }}><div style={textLabel}>Address</div><div style={textValue}>{data.address}</div></div>}
            </div>
          )}

          {(data.breakfastMenu || data.lunchMenu) && (
            <div style={card}>
              <div style={cardTitle}>Menus</div>
              {data.breakfastMenu && <a href={data.breakfastMenu} target="_blank" rel="noreferrer" style={linkRow}><div style={{ ...linkIcon, background: "var(--amber-muted)" }}>🥞</div><div style={linkLabel}>Breakfast Menu</div><div style={linkArrow}>→</div></a>}
              {data.lunchMenu && <a href={data.lunchMenu} target="_blank" rel="noreferrer" style={{ ...linkRow, borderBottom: "none" }}><div style={{ ...linkIcon, background: "var(--sage-muted)" }}>🥗</div><div style={linkLabel}>Lunch Menu</div><div style={linkArrow}>→</div></a>}
            </div>
          )}

          <div style={card}>
            <div style={cardTitle}>Links & Resources</div>
            {[
              { url: data.website, icon: "🌐", label: "Website", bg: "#EAF0FB" },
              { url: data.calendar, icon: "📅", label: "School Calendar", bg: "var(--amber-muted)" },
              { url: data.handbook, icon: "📖", label: "Handbook", bg: "var(--rose-muted)" },
              { url: data.supplyList, icon: "📝", label: "Supply List", bg: "var(--cream-hover)" },
              { url: data.paymentPortal, icon: "💳", label: "Payment Portal", bg: "var(--cream-hover)" },
              { url: data.driveFolder, icon: "📁", label: "Drive Folder", bg: "var(--sage-muted)" },
            ].filter(l => l.url).map((l, i, arr) => (
              <a key={l.label} href={l.url} target="_blank" rel="noreferrer"
                style={{ ...linkRow, borderBottom: i === arr.length - 1 && !data.schoolApp ? "none" : "1px solid var(--border)" }}>
                <div style={{ ...linkIcon, background: l.bg }}>{l.icon}</div>
                <div style={linkLabel}>{l.label}</div>
                <div style={linkArrow}>→</div>
              </a>
            ))}
            {data.schoolApp && (/^https?:\/\//i.test(data.schoolApp) ? (
              <a href={data.schoolApp} target="_blank" rel="noreferrer" style={{ ...linkRow, borderBottom: "none" }}>
                <div style={{ ...linkIcon, background: "var(--sage-muted)" }}>📱</div>
                <div style={linkLabel}>School App</div>
                <div style={linkArrow}>→</div>
              </a>
            ) : (
              <div style={{ ...linkRow, borderBottom: "none", cursor: "default" }}>
                <div style={{ ...linkIcon, background: "var(--sage-muted)" }}>📱</div>
                <div style={linkLabel}>School App</div>
                <div style={{ fontSize: 12, color: "var(--muted)", fontWeight: 600 }}>{data.schoolApp}</div>
              </div>
            ))}
          </div>

          {blocks.length > 0 && (
            <div style={card}>
              <div style={{ ...cardTitle, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span>Notes</span>
                {!readOnly && <a href={data.notionUrl} target="_blank" rel="noreferrer" style={{ fontSize: 11, fontWeight: 700, color: "var(--sage)", textDecoration: "none" }}>Edit in Notion →</a>}
              </div>
              <div style={{ padding: "11px 14px" }}>
                {blocks.map((b, i) => (
                  <div key={i} style={{ marginBottom: i < blocks.length - 1 ? 8 : 0, paddingLeft: b.type === "bulleted_list_item" ? 14 : 0, position: "relative" }}>
                    {b.type === "bulleted_list_item" && <span style={{ position: "absolute", left: 0, color: "var(--sage)", fontWeight: 800 }}>•</span>}
                    <RichText text={b.text} urls={b.urls} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <nav style={bottomNav}>
          {NAV_TABS.map(t => (
            <div key={t.id} onClick={() => onTabTap?.(t.id)} style={navItem}>
              <div style={{ fontSize: 22 }}>{t.icon}</div>
              <div style={navLabel}>{t.label}</div>
            </div>
          ))}
        </nav>
      </div>

      {showEdit && (
        <>
          <div onClick={() => setShowEdit(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 200 }} />
          <div style={{ position: "fixed", bottom: 0, left: "50%", transform: "translateX(-50%)", width: "100%", maxWidth: "var(--app-max-width)", background: "var(--white)", borderRadius: "20px 20px 0 0", zIndex: 201, paddingBottom: 24 }}>
            <div style={{ display: "flex", justifyContent: "center", padding: "12px 0 4px" }}><div style={{ width: 36, height: 4, borderRadius: 2, background: "var(--border)" }} /></div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 20px 16px", borderBottom: "1px solid var(--border)" }}>
              <button onClick={() => setShowEdit(false)} style={sheetBtn}>Cancel</button>
              <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 16, fontWeight: 600, color: "var(--charcoal)" }}>Edit School</span>
              <button onClick={handleSave} disabled={saving} style={{ ...sheetBtn, color: saving ? "var(--muted)" : "var(--sage)", fontWeight: 800 }}>{saving ? "Saving…" : "Save"}</button>
            </div>
            <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
              {[
                { key: "specialDays", label: "Special Days", rows: 3 },
                { key: "notes", label: "Notes", rows: 3 },
                { key: "teachers", label: "Teachers", rows: 2 },
                { key: "breakfastMenu", label: "Breakfast Menu URL", rows: 1 },
                { key: "lunchMenu", label: "Lunch Menu URL", rows: 1 },
              ].map((f, i, arr) => (
                <div key={f.key} style={{ padding: "12px 20px", borderBottom: i < arr.length - 1 ? "1px solid var(--border)" : "none" }}>
                  <div style={sheetLabel}>{f.label}</div>
                  <textarea rows={f.rows} value={form[f.key]} onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                    style={{ width: "100%", border: "none", outline: "none", fontSize: 15, fontWeight: 500, color: "var(--charcoal)", fontFamily: "inherit", resize: "none", background: "transparent", lineHeight: 1.5 }} />
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </>
  );
}

const card = { background: "var(--white)", borderRadius: 14, border: "1px solid var(--border)", overflow: "hidden" };
const cardTitle = { fontSize: 11, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--muted)", padding: "11px 14px", borderBottom: "1px solid var(--border)" };
const grid2 = { display: "grid", gridTemplateColumns: "1fr 1fr" };
const cell = { padding: "10px 14px", borderBottom: "1px solid var(--border)", borderRight: "1px solid var(--border)" };
const cellLabel = { fontSize: 10, fontWeight: 800, letterSpacing: "0.07em", textTransform: "uppercase", color: "var(--muted)", marginBottom: 3 };
const cellValue = { fontSize: 15, fontWeight: 600, color: "var(--charcoal)", lineHeight: 1.5 };
const textRow = { padding: "11px 14px", borderBottom: "1px solid var(--border)" };
const textLabel = { fontSize: 10, fontWeight: 800, letterSpacing: "0.07em", textTransform: "uppercase", color: "var(--muted)", marginBottom: 4 };
const textValue = { fontSize: 15, fontWeight: 500, color: "var(--charcoal)", lineHeight: 1.6 };
const linkRow = { display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderBottom: "1px solid var(--border)", cursor: "pointer", textDecoration: "none" };
const linkIcon = { width: 30, height: 30, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, flexShrink: 0 };
const linkLabel = { flex: 1, fontSize: 15, fontWeight: 600, color: "var(--charcoal)" };
const linkArrow = { fontSize: 14, fontWeight: 700, color: "var(--sage)" };
const bottomNav = { position: "fixed", bottom: 0, left: "50%", transform: "translateX(-50%)", width: "100%", maxWidth: "var(--app-max-width)", background: "var(--white)", borderTop: "1px solid var(--border)", display: "flex", padding: "10px 0 20px", zIndex: 100 };
const navItem = { flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 3, cursor: "pointer", padding: "4px 0" };
const navLabel = { fontSize: 10, fontWeight: 700, color: "var(--muted)", textAlign: "center", lineHeight: 1.2 };
const sheetBtn = { background: "none", border: "none", fontSize: 14, fontWeight: 700, color: "var(--muted)", cursor: "pointer", fontFamily: "inherit" };
const sheetLabel = { fontSize: 11, fontWeight: 800, letterSpacing: "0.07em", textTransform: "uppercase", color: "var(--muted)", marginBottom: 6 };
